/**
 * Lets a route put its own content into the app header.
 *
 * The header sits outside the scroll container, so what a page puts here stays
 * on screen while the page scrolls -- which is the point: the range selector
 * governs every figure below it, and scrolling past it left the reader looking
 * at numbers with no way to see, or change, which months they cover.
 */
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

interface PageHeaderValue {
  content: ReactNode;
  setContent: (node: ReactNode) => void;
}

const PageHeaderContext = createContext<PageHeaderValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ReactNode>(null);
  return (
    <PageHeaderContext.Provider value={{ content, setContent }}>
      {children}
    </PageHeaderContext.Provider>
  );
}

/** Read side, for the layout. */
export function usePageHeaderContent(): ReactNode {
  return useContext(PageHeaderContext)?.content ?? null;
}

/**
 * Write side, for a page.
 *
 * `deps` decides when the header is rebuilt -- pass the values it renders, not
 * the node itself: a fresh element every render would set state on every render
 * and loop. Clearing on unmount is what stops one page's header from lingering
 * over the next.
 */
export function usePageHeader(build: () => ReactNode, deps: unknown[]) {
  const ctx = useContext(PageHeaderContext);
  const setContent = ctx?.setContent;
  useEffect(
    () => {
      if (!setContent) return;
      setContent(build());
      return () => setContent(null);
      // build is intentionally excluded: it is a new closure every render, and
      // deps names the values that actually change what it produces.
    },
    deps.concat(setContent as unknown),
  );
}
